import { Stack, styled } from "@mui/material";
import { useParams } from "react-router";
import { TCrumbProps, TreeBreadcrumb } from "./tree-breadcrumbs";
import { useAppSelector } from "../../../../Redux/redux-store";
import { GetObject, GetObjectHierarchyDto } from "../../../../Redux/Reducers/redux-slice-asset";
import { TPageHeadRoute } from "../page-head";
import { GetTitleRouterPath } from "../../../../Utils/helpers";
import { routePaths } from "../../../../Router/router";
import AssetIcon from "../../../Misc/AssetIcon/asset-icon";
import { useEffect, useState } from "react";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { assetService, cacheService, ingestService, orderService } from "../../../../Core/Api/services";
import { PlatformMode } from "../../../../Core/Api/Types/platform-mode";
import { IsDriveObject } from "../../../../Core/Utils/asset-object-utils";

interface IPageHeadBreadcrumbProps {
  route: TPageHeadRoute;
}

const PageHeadBreadcrumbs: React.FC<IPageHeadBreadcrumbProps> = (props) => {
  const { route } = props;
  const [treeObj, setTreeObj] = useState<TCrumbProps[]>([]);
  const graph = useAppSelector(GetObject);
  const treeData = useAppSelector(GetObjectHierarchyDto);
  const { orderId, id } = useParams();

  const platformMode = cacheService.GetPlatformMode();

  useEffect(() => {
    const breadcrumb = [] as TCrumbProps[];

    let isDrive = platformMode === PlatformMode.Browser || (graph && IsDriveObject(graph));
    let isAdam = platformMode === PlatformMode.Adam;
    let pageTitle = route.element.props.pageTitle;

    if (isDrive) pageTitle = "Drive";
    if (isAdam) pageTitle = "Adam";

    // We push root link in breadcrumb
    breadcrumb.push({
      name: pageTitle,
      link: isAdam
        ? `${routePaths.inventory.path}/${routePaths.inventorySourceAdam.path}`
        : isDrive
        ? routePaths.drive.path
        : route.element.props.rootLink,
    });

    switch (route.element.props.rootLink) {
      case routePaths.inventory.path:
      case routePaths.asset.path:
      case routePaths.catalog.path:
        if (graph === undefined) return;

        //If current object is a title, we display only the title in breadcrumb
        if (graph?.layer === ObjectLayer.Title) {
          breadcrumb.push({ name: graph.name, link: `${GetTitleRouterPath()}/${graph.id}` });
        }
        // Else we load hierarchy tree and display hierarchy (title included in hierarchy)
        else {
          treeData?.hierarchyTree.forEach((item) => {
            switch (item.objectLayer) {
              case "Title":
                return breadcrumb.push({
                  name: `${item.objectName}`,
                  link: `${GetTitleRouterPath()}/${item.objectId}`,
                });
              case "Content":
              case "SubContent":
                return breadcrumb.push({
                  name: `${item.objectName}`,
                  link: `${isDrive ? routePaths.drive.path : routePaths.asset.path}/${item.objectId}`,
                });
              case "Group":
                // Adam exception
                if (isAdam) return;
                return breadcrumb.push({
                  name: `${item.objectName}`,
                  link: `${isDrive ? routePaths.drive.path : routePaths.asset.path}/${item.objectId}`,
                  icon: <AssetIcon type={item.objectType!} colored />,
                });
              case "Asset":
              case "Clip":
                return breadcrumb.push({
                  name: isAdam ? item.objectName ?? "" : `${item.objectId}`,
                  link: `${isDrive ? routePaths.drive.path : routePaths.asset.path}/${item.objectId}`,
                  icon: <AssetIcon type={item.objectType!} colored />,
                });
              default:
                break;
            }
          });
        }
        setTreeObj(breadcrumb);
        break;
      case routePaths.ordersBase.path:
      case routePaths.ordersOverview.path:
        if (orderId === null) return;
        breadcrumb.push({ name: `Order ${orderId}`, link: `${route.element.props.rootLink}/${parseInt(orderId!)}` });
        setTreeObj(breadcrumb);
        break;
      case routePaths.collections.path:
      case routePaths.storefront.path:
        if (id === null) return;
        assetService.GetCollection(parseInt(id!)).then((data) => {
          breadcrumb.push({ name: `${data.name}`, link: `${route.element.props.rootLink}/${data.id}` });
          setTreeObj(breadcrumb);
        });
        break;
      case routePaths.customers.path:
        if (id === null) return;
        orderService.GetCustomerById(parseInt(id!)).then((response) => {
          if (response.customer) {
            breadcrumb.push({
              name: `${response.customer.name}`,
              link: `${route.element.props.rootLink}/${response.customer.id}`,
            });
            setTreeObj(breadcrumb);
          }
        });
        break;
      case routePaths.contractsAndRights.path:
        if (id === null) return;
        breadcrumb.push({
          name: `${id}`,
          link: `${route.element.props.rootLink}/${id}`,
        });
        setTreeObj(breadcrumb);
        break;
      case routePaths.providers.path:
        if (id === null) return;
        ingestService.GetProviderById(parseInt(id!)).then((response) => {
          if (response.provider) {
            breadcrumb.push({
              name: `${response.provider.labName}`,
              link: `${route.element.props.rootLink}/${response.provider.id}`,
            });
            setTreeObj(breadcrumb);
          }
        });
        break;
    }
  }, [graph, treeData, orderId, id]);

  //   return !isLoading ? <TreeBreadcrumb treeObj={treeObj} /> : <TreeBreadcrumbSkeleton></TreeBreadcrumbSkeleton>;
  return <TreeBreadcrumb treeObj={treeObj!} />;
};

export default PageHeadBreadcrumbs;

const PageHeadBreadcrumbsStyled = styled(Stack, { shouldForwardProp: (prop) => prop !== "variant" })<{
  variant?: string;
}>(({ theme }) => ({}));
